import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button, Card, Checkbox, Field, Notice } from '@ailab/ui';
import type { z } from 'zod';
import { authenticatedResponseSchema, signupStatusSchema } from '@ailab/contracts';
import { useRefreshMe } from '../../lib/account';
import { ApiRequestError, apiRequest, errorMessage, rememberCsrf } from '../../lib/http';
import styles from './account.module.css';

/** Versión de las condiciones que muestra el formulario. Debe coincidir con el servidor. */
export const TERMS_VERSION = '2026-09-06';
const TERMS_URL = `https://ailendra.com/ailab/legal/${TERMS_VERSION}.html`;

type Pending = z.infer<typeof signupStatusSchema>;

export function SignupPage() {
  const navigate = useNavigate();
  const refreshMe = useRefreshMe();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    skoolName: '',
    password: '',
    passwordConfirm: '',
    invitationCode: '',
    termsAccepted: false,
  });
  const [pending, setPending] = useState<Pending>();
  const [code, setCode] = useState('');
  const [newEmail, setNewEmail] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [info, setInfo] = useState<string>();

  // Si este navegador tiene un alta a medias, se retoma en el paso del código.
  useEffect(() => {
    apiRequest('/auth/signup', signupStatusSchema)
      .then(setPending)
      .catch(() => undefined);
  }, []);

  const set = (key: keyof typeof form) => (event: { target: HTMLInputElement }) =>
    setForm((current) => ({
      ...current,
      [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    }));

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    setInfo(undefined);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught));
      if (caught instanceof ApiRequestError && caught.code === 'expired') setPending(undefined);
    } finally {
      setBusy(false);
    }
  }

  function showSent(status: Pending) {
    setPending(status);
    setInfo(
      status.mailUncertain
        ? 'No hemos podido confirmar el envío del correo. Si recibes el código, puedes usarlo. Si no llega, solicita otro en un minuto.'
        : `Te hemos enviado un código de seis dígitos a ${status.email}. Caduca en 10 minutos.`,
    );
  }

  function start(event: FormEvent) {
    event.preventDefault();
    if (form.password !== form.passwordConfirm) {
      setError('Las dos contraseñas no coinciden.');
      return;
    }
    if (!form.termsAccepted) {
      setError('Revisa y acepta las condiciones antes de crear tu cuenta.');
      return;
    }
    const { passwordConfirm: _confirm, ...body } = form;
    void run(async () => {
      showSent(
        await apiRequest('/auth/signup', signupStatusSchema, {
          method: 'POST',
          body: { ...body, termsVersion: TERMS_VERSION },
        }),
      );
    });
  }

  function verify(event: FormEvent) {
    event.preventDefault();
    void run(async () => {
      const result = await apiRequest('/auth/signup/verify', authenticatedResponseSchema, {
        method: 'POST',
        body: { code },
      });
      rememberCsrf(result.csrfToken);
      await refreshMe();
      await navigate({ to: '/cuenta' });
    });
  }

  function resend(email?: string) {
    void run(async () => {
      showSent(
        await apiRequest('/auth/signup/resend', signupStatusSchema, {
          method: 'POST',
          body: email ? { email } : {},
        }),
      );
      setNewEmail(undefined);
      setCode('');
    });
  }

  function cancel() {
    void run(async () => {
      await apiRequest('/auth/signup', null, { method: 'DELETE' });
      setPending(undefined);
    });
  }

  if (pending) {
    return (
      <div className={styles.narrow}>
        <h1>Confirma tu email</h1>
        <p className={styles.lead}>Tu cuenta no se crea hasta que escribas el código.</p>
        <Card>
          {newEmail === undefined ? (
            <form className={styles.form} onSubmit={verify} noValidate>
              {info ? <Notice tone="info">{info}</Notice> : null}
              <Field
                label="Código de seis dígitos"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              {error ? <Notice tone="error">{error}</Notice> : null}
              <Button type="submit" busy={busy}>
                Confirmar y entrar
              </Button>
              <div className={styles.row}>
                <Button type="button" variant="ghost" disabled={busy} onClick={() => resend()}>
                  Reenviar código
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => setNewEmail(pending.email)}
                >
                  Corregir email
                </Button>
                <Button type="button" variant="ghost" disabled={busy} onClick={cancel}>
                  Volver al registro
                </Button>
              </div>
            </form>
          ) : (
            <form
              className={styles.form}
              onSubmit={(event) => {
                event.preventDefault();
                resend(newEmail);
              }}
              noValidate
            >
              <Field
                label="Email"
                type="email"
                autoComplete="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              {error ? <Notice tone="error">{error}</Notice> : null}
              <div className={styles.row}>
                <Button type="submit" busy={busy}>
                  Enviar código nuevo
                </Button>
                <Button type="button" variant="secondary" onClick={() => setNewEmail(undefined)}>
                  Volver al código
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.narrow}>
      <h1>Crear cuenta</h1>
      <p className={styles.lead}>Necesitas un código de invitación de AiLendra Labs.</p>
      <Card>
        <form className={styles.form} onSubmit={start} noValidate>
          <div className={styles.grid}>
            <Field
              label="Nombre"
              autoComplete="given-name"
              required
              value={form.firstName}
              onChange={set('firstName')}
            />
            <Field
              label="Apellidos"
              autoComplete="family-name"
              required
              value={form.lastName}
              onChange={set('lastName')}
            />
          </div>
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={set('email')}
          />
          <Field
            label="Nombre en Skool"
            hint="Tal como aparece en la comunidad, para identificarte."
            required
            value={form.skoolName}
            onChange={set('skoolName')}
          />
          <Field
            label="Contraseña"
            type="password"
            autoComplete="new-password"
            hint="Al menos 10 caracteres."
            required
            value={form.password}
            onChange={set('password')}
          />
          <Field
            label="Repite la contraseña"
            type="password"
            autoComplete="new-password"
            required
            value={form.passwordConfirm}
            onChange={set('passwordConfirm')}
          />
          <Field
            label="Código de invitación"
            placeholder="AILAB-XXXX-XXXX-XXXX"
            autoComplete="off"
            required
            value={form.invitationCode}
            onChange={set('invitationCode')}
          />
          <Checkbox checked={form.termsAccepted} onChange={set('termsAccepted')}>
            Acepto las{' '}
            <a
              className={styles.inlineLink}
              href={`${TERMS_URL}#condiciones`}
              target="_blank"
              rel="noopener noreferrer"
            >
              condiciones de uso
            </a>{' '}
            y he leído la{' '}
            <a
              className={styles.inlineLink}
              href={`${TERMS_URL}#privacidad`}
              target="_blank"
              rel="noopener noreferrer"
            >
              información de privacidad
            </a>
            , incluido el tratamiento de mis archivos por proveedores de IA y el acceso
            administrativo necesario.
          </Checkbox>
          {error ? <Notice tone="error">{error}</Notice> : null}
          <Button type="submit" busy={busy}>
            Crear cuenta
          </Button>
        </form>
        <div className={styles.links}>
          <Link to="/entrar">Ya tengo cuenta</Link>
        </div>
      </Card>
    </div>
  );
}
